<template>
  <v-container>
    <h1 class="text-h4 mb-6">Enterprise Subscription</h1>

    <div v-if="loading" class="text-center pa-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-4">Loading subscription details...</p>
    </div>

    <v-alert v-if="errorMessage" type="error" prominent class="mb-4">{{ errorMessage }}</v-alert>
    <v-alert v-if="successMessage" type="success" prominent class="mb-4">{{ successMessage }}</v-alert>

    <v-row v-if="!loading">
      <!-- Left Column: Quote Details -->
      <v-col cols="12" md="6" v-if="quoteData && billingAddress && tierData">
        <!-- NEW: Display Current Subscription if available -->
        <v-card class="mb-4" v-if="currentSubscription">
          <v-card-title class="text-h6 orange--text text--darken-3">Current Subscription</v-card-title>
          <v-card-text>
            <v-alert type="warning" dense text class="mb-4">
              You are currently subscribed to the {{ currentSubscription.tier_name }} tier. Upgrading to Enterprise will change your current subscription.
            </v-alert>
            <div class="mb-2">
              <strong>Plan:</strong> {{ currentSubscription.tier_name }}
            </div>
            <div class="mb-2">
              <strong>Monthly Cost:</strong> {{ formatPrice(currentSubscription.tier_monthly_price) }}
            </div>
            <div v-if=currentSubscription.tier_monthly_price class="mb-2">
              <strong>Base Usage Cost:</strong> {{ formatPrice(currentSubscription.tier_usage_price) }}
            </div>
            <div class="mb-2">
              <strong>Status:</strong> {{ currentSubscription.status }}
            </div>
            <div class="mb-2">
              <strong>Renews On:</strong> {{ formatDate(currentSubscription.current_period_end) }}
            </div>
            <div v-if="currentSubscription.payment_method_brand" class="mb-2">
              <strong>Payment Method:</strong> {{ currentSubscription.payment_method_brand }} ending in {{ currentSubscription.payment_method_last4 }}
            </div>
          </v-card-text>
        </v-card>
        <!-- End NEW -->

        <v-card class="mb-4">
          <v-card-title class="text-h6">Bill To</v-card-title>
          <v-card-text>
            <div class="mb-2">
              <strong>Billing Email:</strong> {{ billingAddress.billing_email }}
            </div>
            <div class="mb-2">
              <strong>Company Name:</strong> {{ billingAddress.organization || 'N/A' }}
            </div>
            <div class="mb-2">
              <strong>Company Address:</strong><br>
              {{ billingAddress.street_1 }}<br>
              <span v-if="billingAddress.street_2">{{ billingAddress.street_2 }}<br></span>
              {{ billingAddress.city }}, {{ billingAddress.state }} {{ billingAddress.postal_code }}<br>
              {{ billingAddress.country }}
            </div>
            <div v-if="billingAddress.vat_number" class="mb-2">
              <strong>VAT Number:</strong> {{ billingAddress.vat_number }}
            </div>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="text-h6">Subscription Details</v-card-title>
          <v-card-text>
            <div class="mb-2">
              <strong>Plan:</strong> {{ tierData.name }}
            </div>
            <div class="mb-2">
              <strong>Monthly Subscription Cost:</strong> {{ formatPrice(quoteData.base_amount) }}
            </div>
            <div class="mb-2">
              <strong>Per Scan Cost:</strong> {{ formatPrice(quoteData.usage_unit_price) }}
            </div>
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-title class="text-h6">Total Due Today</v-card-title>
          <v-card-text>
            <div v-if="currentSubscription && typeof netProrationAmount === 'number'">
              <div class="d-flex justify-space-between font-weight-bold text-h5 primary--text">
                <span>Amount Due Today (Net Proration):</span>
                <span>{{ formatPrice(netProrationAmount) }}</span>
              </div>
              <div class="text-caption mt-1">
                This is the net prorated amount due for the current billing cycle change.
                <span v-if="netProrationAmount < 0">You will receive a credit.</span>
                <span v-else-if="netProrationAmount > 0">You will be charged this amount.</span>
                <span v-else>No additional charge or credit for the change.</span>
              </div>
            </div>
            <div v-else-if="quoteData">
              <div class="text-h5 primary--text">
                {{ formatPrice(quoteData.base_amount) }}
              </div>
              <div class="text-caption mt-1">
                First month's subscription fee
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Column: Payment Form -->
      <v-col cols="12" md="6" fill-height>
        <v-card class="d-flex flex-column stikcy">
          <v-card-title class="text-h6">Payment Information</v-card-title>
          <v-card-text class="flex-grow-1 justify-end">
            <!-- Loading state for Stripe -->
            <div v-if="!stripeLoaded" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-4">Loading Secure Payment Form...</p>
            </div>

            <!-- Payment form -->
            <div v-show="stripeLoaded">
              <div id="payment-element">
                <!-- Stripe Payment Element will be mounted here -->
              </div>
              <v-btn
                @click="handleSubmit"
                class="mt-4 str-btn-primary"
                block
                :disabled="!stripe || submitting"
                :loading="submitting"
              >
                Subscribe
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth0 } from '@auth0/auth0-vue';
// import { useStore } from 'vuex';
import axios from 'axios';

const { getAccessTokenSilently, user: auth0User } = useAuth0();
// const store = useStore();

const stripe = ref(null);
const elements = ref(null);
const clientSecret = ref(null);
const loading = ref(true);
const stripeLoaded = ref(false);
const submitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

// Quote and billing data
const quoteData = ref(null);
const billingAddress = ref(null);
const tierData = ref(null);
const currentSubscription = ref(null); // New reactive variable
const netProrationAmount = ref(0.0); // New reactive variable for Stripe-calculated net proration


const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

// New utility function to format dates
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp * 1000); // Stripe timestamps are in seconds
  return date.toLocaleDateString();
};


const mountStripeElement = async () => {
  if (!stripe.value || !clientSecret.value || stripeLoaded.value) {
    return;
  }

  // Use setTimeout to ensure DOM is fully rendered
  await new Promise(resolve => setTimeout(resolve, 100));

  // Check if element exists
  const paymentElementDiv = document.getElementById('payment-element');

  if (!paymentElementDiv) {
    console.error('Payment element div not found');
    errorMessage.value = 'Failed to initialize payment form.';
    return;
  }

  try {
    // Create elements instance
    elements.value = stripe.value.elements({ clientSecret: clientSecret.value });

    // Create and mount payment element
    const paymentElement = elements.value.create('payment');
    paymentElement.mount('#payment-element');

    stripeLoaded.value = true;
  } catch (error) {
    console.error('Error mounting Stripe element:', error);
    errorMessage.value = 'Failed to load payment form.';
  }
};

    const fetchQuoteDetails = async (token, headers) => {
      try {
        // Fetch user details to get the organization from the database
        const userResponse = await axios.get(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/users/${auth0User.value.sub}`,
          { headers }
        );
        const userOrganization = userResponse.data.organization;

        // Fetch quote
        const quoteResponse = await axios.get(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/quote/${auth0User.value.sub}`,
          { headers }
        );
        quoteData.value = quoteResponse.data;

        // Fetch billing address
        const billingResponse = await axios.get(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/address/${auth0User.value.sub}`,
          { headers }
        );

        billingAddress.value = {
          ...billingResponse.data,
          organization: userOrganization // Use the organization fetched directly from the user table
        };

        // Fetch tiers and find Enterprise
        const tiersResponse = await axios.get(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/tiers`,
          { headers }
        );

        tierData.value = tiersResponse.data.find(tier => tier.name === 'Enterprise');

        if (!tierData.value) {
          throw new Error('Enterprise tier not found');
        }

      } catch (error) {
        console.error('Error fetching subscription details:', error);
        errorMessage.value = error.response?.data?.detail || 'Failed to load subscription details.';
        throw error;
      }
    };
onMounted(async () => {
  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    // Initialize Stripe first
    stripe.value = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);

    if (!stripe.value) {
      throw new Error('Failed to load Stripe');
    }

    // Create Stripe SetupIntent
    const response = await axios.post(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/create-setup-intent/${auth0User.value.sub}`,
      {},
      { headers }
    );
    clientSecret.value = response.data.client_secret;

    if (!clientSecret.value) {
      throw new Error('Backend did not return a valid client secret');
    }

    // Fetch all quote and billing details
    await fetchQuoteDetails(token, headers);

    // NEW: Fetch current subscription details
    const currentSubResponse = await axios.get(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/current-subscription/${auth0User.value.sub}`,
      { headers }
    );

    if (currentSubResponse.data && currentSubResponse.data.message !== "No active subscription found for this user.") {
      currentSubscription.value = currentSubResponse.data;

      // If there's an existing subscription, fetch proration preview
      try {
        const prorationPreviewResponse = await axios.get(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/proration-preview/${auth0User.value.sub}`,
          { headers }
        );
        netProrationAmount.value = prorationPreviewResponse.data.net_proration_amount;
      } catch (prorationError) {
        console.error('Error fetching proration preview:', prorationError);
        // Don't block loading if proration preview fails, just log error
      }
    }


  } catch (error) {
    console.error('Error loading subscription conversion:', error);
    if (!errorMessage.value) {
      errorMessage.value = error.response?.data?.detail || error.message || 'Failed to load payment setup.';
    }
  } finally {
    // Set loading to false
    loading.value = false;

    // Mount Stripe after a short delay to ensure DOM is rendered
    await nextTick();
    await mountStripeElement();
  }
});

const handleSubmit = async () => {
  if (!stripe.value || !elements.value || !clientSecret.value) {
    errorMessage.value = 'Stripe is not initialized.';
    return;
  }

  submitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  const { setupIntent, error } = await stripe.value.confirmSetup({
    elements: elements.value,
    confirmParams: {
      return_url: `${window.location.origin}/console/account`,
    },
    redirect: 'if_required'
  });

  if (error) {
    errorMessage.value = error.message;
  } else if (setupIntent) {
    if (setupIntent.status === 'succeeded') {
      successMessage.value = 'Payment method successfully set up!';

      // Call backend to accept the quote and complete subscription setup
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        await axios.post(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/billing/accept-enterprise-quote/${auth0User.value.sub}`,
          { payment_method_id: setupIntent.payment_method },
          { headers }
        );
        successMessage.value = 'Enterprise subscription successfully activated!';
        window.location.href = '/console/account';
      } catch (backendError) {
        console.error('Error activating subscription:', backendError);
        errorMessage.value = backendError.response?.data?.detail || 'Failed to activate subscription.';
        successMessage.value = '';
      }

    } else {
      errorMessage.value = `SetupIntent status: ${setupIntent.status}`;
    }
  }
  submitting.value = false;
};
</script>

<style scoped>
#payment-element {
  width: 100%;
  min-height: 50px;
  margin-bottom: 20px;
}
</style>
<style scoped lang="scss">
@import "@/assets/styles/StrVariables.scss";
@import "@/assets/styles/StrButtons.scss";
</style>